import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Lock, Eye, EyeOff, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from '../i18n';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Login({ onLogin, onBack, embedded }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('password'); // 'password' or 'email'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { email, password };
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, payload);
      onLogin(res.data.token, res.data.role);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
    } catch { /* silently succeed for security */ }
    setForgotSent(true);
  };

  // Embedded mode — just the form, no wrapper
  if (embedded) {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoFocus /></div>
        <div>
          <Label className="text-xs">Password</Label>
          <div className="relative mt-1"><Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" /><button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button></div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full gap-2" disabled={loading || !password || !email}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}{loading ? 'Signing in...' : 'Sign In with Email'}</Button>
      </form>
    );
  }

  return (
    <div className="h-screen w-screen flex bg-background overflow-hidden">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(16,185,129,0.2) 0%, transparent 50%)',
        }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div className="relative z-10">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors">
              <ArrowRight className="h-3.5 w-3.5 rotate-180" /> Back to home
            </button>
          )}
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            LodgiFlow
          </h1>
          <p className="text-slate-400 mt-2 text-lg">Your tech stack, finally under control</p>
        </div>
        <div className="relative z-10 space-y-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <div className="w-3 h-3 rounded-full bg-blue-400" />
            </div>
            <div>
              <p className="font-semibold text-sm">Visualize Your Stack</p>
              <p className="text-slate-400 text-xs mt-0.5">Interactive maps of every platform, product, and integration across all properties.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <div className="w-3 h-3 rounded-full bg-emerald-400" />
            </div>
            <div>
              <p className="font-semibold text-sm">Track Compliance</p>
              <p className="text-slate-400 text-xs mt-0.5">Brand standards, vendor scorecards, contract renewals — all in one place.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <div className="w-3 h-3 rounded-full bg-amber-400" />
            </div>
            <div>
              <p className="font-semibold text-sm">Optimize Spend</p>
              <p className="text-slate-400 text-xs mt-0.5">Cost tracking, gap analysis, and consolidation insights across your portfolio.</p>
            </div>
          </div>
        </div>
        <p className="relative z-10 text-xs text-slate-500">&copy; {new Date().getFullYear()} LodgiFlow. All rights reserved.</p>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>LodgiFlow</h1>
            <p className="text-sm text-muted-foreground mt-1">Your tech stack, finally under control</p>
          </div>

          {showForgot ? (
            /* Forgot password form */
            <div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Reset Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your email and we&apos;ll send a reset link.</p>
              {forgotSent ? (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
                  If an account exists for <strong>{forgotEmail}</strong>, a reset link has been sent.
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@company.com" autoFocus />
                  </div>
                  <Button type="submit" className="w-full" disabled={!forgotEmail}>Send Reset Link</Button>
                </form>
              )}
              <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="text-xs text-muted-foreground hover:text-foreground mt-4 block mx-auto">
                Back to sign in
              </button>
            </div>
          ) : (
            /* Login form */
            <div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Welcome back</h2>
              <p className="text-sm text-muted-foreground mb-6">Sign in to your LodgiFlow account</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoFocus data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Password</Label>
                    <button type="button" onClick={() => setShowForgot(true)} className="text-[10px] text-primary hover:underline">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative mt-1">
                    <Input
                      data-testid="login-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Your password"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>

                {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}

                <Button type="submit" data-testid="login-submit-button" className="w-full gap-2" disabled={loading || !password}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
